import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  DASHBOARD_DEFAULT_ACTIVE_TAB,
  DEFAULT_OPENROUTER_MODEL,
  DEFAULT_UPLOAD_KIND,
  DEFAULT_UPLOAD_SOURCE,
  DEFAULT_REPORT_FOCUS,
  AVAILABLE_DOCTORS,
  INITIAL_CHAT_MESSAGES,
  INITIAL_CONSULTATIONS,
  INITIAL_MEDICATIONS,
  INITIAL_PROVIDERS,
  INITIAL_RECORDS,
  INITIAL_REPORTS,
  INITIAL_TASKS,
  PROVIDER_DISCONNECTED_LABEL,
  REPORT_TEMPLATES,
} from './constants'
import type { DashboardTabId } from './constants'
import type { AuthMode } from '../auth/authStorage'
import { clearAppLocalStorage } from '../auth/authStorage'
import { usePersistedState } from '../auth/usePersistedState'
import {
  isAgentApiEnabled,
  requestChatAnswerViaAgentApi,
  syncSettingsWithAgentApi,
  uploadAndParseRecordViaAgentApi,
} from './agentClient'
import type { AgentWorkspaceContext } from './agentClient'
import { requestOpenRouterCompletion } from './openRouterClient'
import { parseUploadedPdfRecord } from './recordParsing'
import type {
  ChatMessage,
  ChatThread,
  Consultation,
  ConsultationDraft,
  DashboardMetrics,
  HealthRecord,
  Medication,
  MedicationDraft,
  OpenRouterSettings,
  PersonalProfile,
  Provider,
  Report,
  ReportNarrative,
  ReportGenerationRequest,
  ReportRecordScope,
  Task,
  ActionResult,
  UploadDraft,
} from './types'
import type { SetStateAction } from 'react'

interface LegacyHealthRecord {
  fileSize?: string
  fileSizeBytes?: number | null
  id: string
  kind: HealthRecord['kind']
  parseError?: string | null
  parseSummary?: string | null
  source: string
  status: HealthRecord['status']
  textPreview?: string | null
  title: string
  updatedAt: string
}

type PersistedHealthRecord = HealthRecord | LegacyHealthRecord

interface DashboardActions {
  clearAllChats: () => void
  clearOpenRouterSettings: (model: string) => Promise<void>
  deleteChatThread: (threadId: string) => void
  handleAddCustomProvider: (providerName: string) => ActionResult
  handleDeleteRecord: (recordId: string) => ActionResult
  handleAddMedication: (draft: MedicationDraft) => void
  handleAddRecord: (draft: UploadDraft) => Promise<ActionResult>
  handleBookConsultation: (draft: ConsultationDraft) => ActionResult
  handleGenerateReport: (request: ReportGenerationRequest) => Promise<void>
  handleUpdateReportMetadata: (
    reportId: string,
    metadata: { createdAt: string; focus: string; title: string }
  ) => ActionResult
  handleSendMessage: (text: string) => void
  renameChatThread: (threadId: string, nextTitle: string) => void
  resetChats: () => void
  resetLocalStorage: () => void
  savePersonalProfile: (profile: PersonalProfile) => void
  saveOpenRouterSettings: (settings: OpenRouterSettings) => Promise<void>
  startNewChat: () => void
  switchChatThread: (threadId: string) => void
  toggleProvider: (providerId: string) => Promise<void>
  toggleTask: (taskId: string) => void
}

interface DashboardState {
  actions: DashboardActions
  activeTab: string
  activeChatThreadId: string
  agentApiEnabled: boolean
  chatLoading: boolean
  chatMessages: ChatMessage[]
  chatThreads: ChatThread[]
  consultations: Consultation[]
  isOpenRouterConfigured: boolean
  medications: Medication[]
  metrics: DashboardMetrics
  openRouterSettings: OpenRouterSettings
  personalProfile: PersonalProfile
  providers: Provider[]
  records: HealthRecord[]
  reports: Report[]
  setActiveTab: (tabId: DashboardTabId) => void
  tasks: Task[]
}

const EMPTY_CHAT_MESSAGES: ChatMessage[] = []
const EMPTY_CHAT_THREADS: ChatThread[] = []
const MOCK_CHAT_THREAD_ID = 'mock-chat-thread-1'
const MOCK_CHAT_THREAD_TIMESTAMP = '2026-01-01T00:00:00.000Z'
const INITIAL_MOCK_CHAT_THREADS: ChatThread[] = [
  {
    id: MOCK_CHAT_THREAD_ID,
    title: 'Chat 1',
    messages: INITIAL_CHAT_MESSAGES,
    createdAt: MOCK_CHAT_THREAD_TIMESTAMP,
    updatedAt: MOCK_CHAT_THREAD_TIMESTAMP,
  },
]
const EMPTY_CONSULTATIONS: Consultation[] = []
const EMPTY_MEDICATIONS: Medication[] = []
const EMPTY_PROVIDERS: Provider[] = []
const EMPTY_RECORDS: PersistedHealthRecord[] = []
const EMPTY_REPORTS: Report[] = []
const EMPTY_TASKS: Task[] = []
const DEFAULT_PROFILE_COUNTRY = 'United States'

const KB = 1024
const MB = 1024 * KB
const PROVIDER_SYNC_DELAY_MS = 320
const DEFAULT_MAX_PRIOR_REPORTS = 5
const MAX_PRIOR_REPORTS_LIMIT = 20
const DEFAULT_REPORT_RECORD_SCOPE: ReportRecordScope = 'all-records'
const REPORT_RECORD_SCOPE_LABELS: Record<ReportRecordScope, string> = {
  'all-records': 'all records',
  'all-pdfs': 'all uploaded PDFs',
  'parsed-records': 'parsed records only',
  'parsed-pdfs': 'parsed uploaded PDFs only',
}

function isReportRecordScope(value: string): value is ReportRecordScope {
  return value in REPORT_RECORD_SCOPE_LABELS
}

function createDefaultPersonalProfile(name: string): PersonalProfile {
  const normalizedName = name.trim() || 'Member'
  return {
    fullName: normalizedName,
    preferredName: '',
    dateOfBirth: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: DEFAULT_PROFILE_COUNTRY,
    emergencyContactName: '',
    emergencyContactPhone: '',
    insuranceProvider: '',
  }
}

function normalizePersonalProfile(profile: PersonalProfile, fallbackName: string): PersonalProfile {
  const fallback = createDefaultPersonalProfile(fallbackName)
  const normalizedFullName = profile.fullName.trim()
  const normalizedCountry = profile.country.trim()

  return {
    ...fallback,
    ...profile,
    fullName: normalizedFullName || fallback.fullName,
    preferredName: profile.preferredName.trim(),
    dateOfBirth: profile.dateOfBirth.trim(),
    phone: profile.phone.trim(),
    addressLine1: profile.addressLine1.trim(),
    addressLine2: profile.addressLine2.trim(),
    city: profile.city.trim(),
    state: profile.state.trim(),
    postalCode: profile.postalCode.trim(),
    country: normalizedCountry || DEFAULT_PROFILE_COUNTRY,
    emergencyContactName: profile.emergencyContactName.trim(),
    emergencyContactPhone: profile.emergencyContactPhone.trim(),
    insuranceProvider: profile.insuranceProvider.trim(),
  }
}

function computeDashboardMetrics(
  providers: Provider[],
  records: HealthRecord[],
  reports: Report[],
  tasks: Task[],
  isMock: boolean
): DashboardMetrics {
  const connectedProviders = providers.filter((provider) => provider.connected).length
  const parsedRecords = records.filter((record) => record.status === 'Parsed').length
  const pendingRecords = records.filter((record) => record.status === 'Parsing').length
  const completedTasks = tasks.filter((task) => task.done).length
  const taskProgress = tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0
  const profileBase = isMock ? 70 : 0

  return {
    connectedProviders,
    pendingRecords,
    parsedRecords,
    profileCompleteness: Math.min(97, profileBase + connectedProviders * 4 + parsedRecords * 3),
    taskProgress,
  }
}

function parseLegacyFileSizeToBytes(fileSize: string | undefined): number | null {
  if (!fileSize || fileSize === '—') {
    return null
  }

  const match = /^([\d.]+)\s*(KB|MB)$/i.exec(fileSize.trim())
  if (!match) {
    return null
  }

  const value = Number(match[1])
  if (Number.isNaN(value)) {
    return null
  }

  const unit = match[2].toUpperCase()
  return unit === 'MB' ? Math.round(value * MB) : Math.round(value * KB)
}

function normalizeRecord(record: PersistedHealthRecord): HealthRecord {
  const legacyFileSize = 'fileSize' in record ? record.fileSize : undefined
  const normalizedFileSizeBytes =
    typeof record.fileSizeBytes === 'number'
      ? record.fileSizeBytes
      : parseLegacyFileSizeToBytes(legacyFileSize)

  return {
    id: record.id,
    kind: record.kind,
    source: record.source,
    status: record.status,
    title: record.title,
    updatedAt: record.updatedAt,
    fileSizeBytes: normalizedFileSizeBytes,
    parseError: typeof record.parseError === 'string' ? record.parseError : null,
    parseSummary: typeof record.parseSummary === 'string' ? record.parseSummary : null,
    textPreview: typeof record.textPreview === 'string' ? record.textPreview : null,
  }
}

function isReportTemplateFocus(value: string): value is keyof typeof REPORT_TEMPLATES {
  return value in REPORT_TEMPLATES
}

type ReportTemplateFocus = keyof typeof REPORT_TEMPLATES

const REPORT_FOCUS_SIGNAL_KEYWORDS: Record<ReportTemplateFocus, string[]> = {
  All: [
    'apob',
    'ldl',
    'glucose',
    'a1c',
    'thyroid',
    'ferritin',
    'cortisol',
    'crp',
    'inflammation',
    'immune',
    'sleep',
    'hrv',
    'stress',
    'recovery',
  ],
  Cardiometabolic: ['apob', 'ldl', 'cholesterol', 'glucose', 'a1c', 'triglyceride', 'blood pressure'],
  Hormones: ['thyroid', 'ferritin', 'cortisol', 'testosterone', 'estradiol', 'progesterone', 'antibody'],
  Inflammation: ['crp', 'inflammation', 'immune', 'esr', 'homocysteine', 'recovery'],
  Recovery: ['sleep', 'hrv', 'stress', 'fatigue', 'readiness', 'recovery'],
}

const REPORT_FOCUS_ACTIONS: Record<
  ReportTemplateFocus,
  { high: string; low: string; medium: string }
> = {
  All: {
    high: 'Review the full report with your primary clinician to prioritize cross-domain follow-up.',
    medium: 'Retest key markers across lipids, hormones, inflammation, and recovery in the next cycle.',
    low: 'Continue logging symptoms, sleep, and routines to improve trend-level interpretation.',
  },
  Cardiometabolic: {
    high: 'Recheck lipid and glucose markers in 8-12 weeks to confirm trajectory.',
    medium: 'Continue current nutrition and movement routine tied to cardiometabolic goals.',
    low: 'Share this report with your PCP/cardiology workflow for treatment continuity.',
  },
  Hormones: {
    high: 'Repeat hormone testing at a consistent interval for cleaner trend comparison.',
    medium: 'Track sleep, stress, and symptom notes alongside future hormone labs.',
    low: 'Review endocrine follow-up timing if symptoms drift from current baseline.',
  },
  Inflammation: {
    high: 'Repeat inflammation-focused labs after the next recovery cycle.',
    medium: 'Prioritize sleep consistency and training-load balance until retest.',
    low: 'Keep an eye on symptom flare patterns and annotate likely triggers.',
  },
  Recovery: {
    high: 'Stabilize recovery targets (sleep duration, HRV, resting HR) for the next 2-4 weeks.',
    medium: 'Reduce high-intensity load on low-readiness days.',
    low: 'Log evening routine factors that correlate with improved sleep score.',
  },
}

function createEntityId(prefix: string): string {
  if (typeof globalThis.crypto?.randomUUID !== 'function') {
    throw new Error('[CRYPTO_UUID_UNAVAILABLE] Browser crypto.randomUUID() is required.')
  }

  return `${prefix}-${globalThis.crypto.randomUUID()}`
}

function normalizeOpenRouterApiKey(rawApiKey: string): string {
  return rawApiKey.trim().replace(/^Bearer\s+/i, '')
}

function resolveDefaultOpenRouterSettings(isMock: boolean): OpenRouterSettings {
  const defaultApiKey = isMock ? '' : process.env.REACT_APP_OPENROUTER_REAL_MODE_API_KEY ?? ''
  return {
    apiKey: normalizeOpenRouterApiKey(defaultApiKey),
    model: DEFAULT_OPENROUTER_MODEL,
  }
}

function resolveEffectiveOpenRouterSettings(
  settings: OpenRouterSettings,
  fallback: OpenRouterSettings
): OpenRouterSettings {
  const normalizedApiKey = normalizeOpenRouterApiKey(settings.apiKey)
  const fallbackApiKey = normalizeOpenRouterApiKey(fallback.apiKey)
  const normalizedModel = settings.model.trim()
  const fallbackModel = fallback.model.trim() || DEFAULT_OPENROUTER_MODEL

  return {
    apiKey: normalizedApiKey || fallbackApiKey,
    model: normalizedModel || fallbackModel,
  }
}

function hasOpenRouterCredentials(settings: OpenRouterSettings): boolean {
  return normalizeOpenRouterApiKey(settings.apiKey).length > 0 && settings.model.trim().length > 0
}

function formatClockTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function formatTimestampLabel(date: Date): string {
  return formatClockTime(date)
}

function createAssistantMessage(content: string, date = new Date()): ChatMessage {
  return {
    id: createEntityId('msg-ai'),
    role: 'assistant',
    content,
    timestamp: formatTimestampLabel(date),
  }
}

function normalizeChatThreadTitle(title: string, fallback: string): string {
  const compact = title.trim().replace(/\s+/g, ' ')
  if (!compact) {
    return fallback
  }

  if (compact.length <= 48) {
    return compact
  }

  return `${compact.slice(0, 45)}...`
}

function inferTitleFromChatMessages(messages: ChatMessage[]): string | null {
  const firstUserMessage = messages.find((message) => message.role === 'user')
  if (!firstUserMessage) {
    return null
  }

  const compact = firstUserMessage.content.trim().replace(/\s+/g, ' ')
  if (!compact) {
    return null
  }

  return normalizeChatThreadTitle(compact, 'Chat')
}

function isGenericChatThreadTitle(title: string): boolean {
  const normalized = title.trim().toLowerCase()
  return normalized === 'chat' || normalized === 'new chat' || normalized.startsWith('chat ')
}

function createChatThread(
  title: string,
  messages: ChatMessage[],
  date = new Date()
): ChatThread {
  const timestamp = date.toISOString()
  return {
    id: createEntityId('chat-thread'),
    title: normalizeChatThreadTitle(title, 'Chat'),
    messages,
    createdAt: timestamp,
    updatedAt: timestamp,
  }
}

function updateChatThreadMessages(thread: ChatThread, messages: ChatMessage[]): ChatThread {
  const inferredTitle = isGenericChatThreadTitle(thread.title) ? inferTitleFromChatMessages(messages) : null

  return {
    ...thread,
    title: inferredTitle ?? thread.title,
    messages,
    updatedAt: new Date().toISOString(),
  }
}

function formatRelativeTimestamp(date: Date): string {
  const now = Date.now()
  const diffMs = Math.max(0, now - date.getTime())
  const minutes = Math.floor(diffMs / 60_000)

  if (minutes < 1) {
    return 'Just now'
  }

  if (minutes < 60) {
    return `${minutes}m ago`
  }

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `${hours}h ago`
  }

  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatDeterministicError(
  error: unknown,
  defaultCode: string,
  defaultMessage: string
): string {
  if (error instanceof Error) {
    const message = error.message.trim()
    if (message) {
      return message.startsWith('[') ? message : `[${defaultCode}] ${message}`
    }
  }

  return `[${defaultCode}] ${defaultMessage}`
}

function buildOpenRouterSystemPrompt(
  isMock: boolean,
  connectedProviders: number,
  parsedRecords: number,
  reportsCount: number,
  parsedRecordContext: string
): string {
  return [
    'You are Alberto Health AI, a concise and practical wellness assistant.',
    'You are operating inside a local-first health dashboard prototype.',
    `Mode: ${isMock ? 'mock demo mode' : 'real local account mode'}.`,
    `Current stats: ${connectedProviders} connected provider(s), ${parsedRecords} parsed record(s), ${reportsCount} report(s).`,
    `Recent parsed record context: ${parsedRecordContext}`,
    'Do not claim clinical certainty. Offer practical next steps and reference in-app tabs when relevant.',
  ].join('\n')
}

function buildParsedRecordContext(records: HealthRecord[]): string {
  const summaries = records
    .filter((record) => record.status === 'Parsed')
    .slice(0, 4)
    .map((record) => {
      const summary = record.parseSummary?.trim()
      if (summary) {
        return `${record.title}: ${summary}`
      }

      return `${record.title}: Parsed record with no summary available.`
    })

  if (summaries.length === 0) {
    return 'No parsed records yet.'
  }

  return summaries.join(' | ')
}

function buildAgentWorkspaceContext(
  records: HealthRecord[],
  connectedProviders: number,
  parsedRecords: number,
  reportsCount: number,
  parsedRecordContext: string
): AgentWorkspaceContext {
  const parsedRecordsSample = records
    .filter(
      (record) =>
        record.status === 'Parsed' &&
        typeof record.parseSummary === 'string' &&
        record.parseSummary.trim().length > 0
    )
    .slice(0, 6)
    .map((record) => ({
      title: record.title,
      kind: record.kind,
      summary: record.parseSummary.trim(),
      source: record.source,
      updatedAt: record.updatedAt,
    }))

  return {
    connectedProviders,
    parsedRecords,
    reportsCount,
    parsedRecordContext,
    parsedRecordsSample,
  }
}

function computeDeterministicReportConfidence(focus: string, reportsCount: number): number {
  const seed = `${focus}:${reportsCount}`
  let hash = 0

  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) % 10_000
  }

  return 86 + (hash % 10)
}

function clampPriorReportCount(value: number): number {
  if (!Number.isFinite(value)) {
    return DEFAULT_MAX_PRIOR_REPORTS
  }

  const rounded = Math.round(value)
  return Math.min(MAX_PRIOR_REPORTS_LIMIT, Math.max(1, rounded))
}

function normalizeReportDateInput(rawValue: string | null): string | null {
  if (typeof rawValue !== 'string') {
    return null
  }

  const normalized = rawValue.trim()
  if (!normalized) {
    return null
  }

  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : null
}

function parseReportDateBoundary(
  normalizedDate: string | null,
  boundary: 'start' | 'end'
): Date | null {
  if (!normalizedDate) {
    return null
  }

  const isoDate =
    boundary === 'start'
      ? `${normalizedDate}T00:00:00.000Z`
      : `${normalizedDate}T23:59:59.999Z`
  const parsed = new Date(isoDate)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function parseReportIsoTimestamp(report: Report): Date | null {
  if (typeof report.createdAtIso !== 'string') {
    return null
  }

  const normalized = report.createdAtIso.trim()
  if (!normalized) {
    return null
  }

  const parsed = new Date(normalized)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function isPdfSourcedRecord(record: HealthRecord): boolean {
  return record.source.trim().toLowerCase().includes('pdf')
}

function matchesReportRecordScope(record: HealthRecord, scope: ReportRecordScope): boolean {
  const isParsed = record.status === 'Parsed'
  const isPdfSource = isPdfSourcedRecord(record)

  switch (scope) {
    case 'all-records':
      return true
    case 'all-pdfs':
      return isPdfSource
    case 'parsed-records':
      return isParsed
    case 'parsed-pdfs':
      return isParsed && isPdfSource
  }
}

function toRecordCoverageLabel(record: HealthRecord): string {
  return `${record.title} (${record.source})`
}

function findFocusSignalSummary(focus: ReportTemplateFocus, records: HealthRecord[]): string | null {
  const keywords = REPORT_FOCUS_SIGNAL_KEYWORDS[focus]
  const matchedSignals: string[] = []
  const recordText = records
    .map((record) => `${record.parseSummary ?? ''} ${record.textPreview ?? ''}`.toLowerCase())
    .join(' ')

  keywords.forEach((keyword) => {
    if (matchedSignals.length >= 3) {
      return
    }
    if (recordText.includes(keyword)) {
      matchedSignals.push(keyword.toUpperCase())
    }
  })

  if (matchedSignals.length === 0) {
    return null
  }

  return `Detected focus signals: ${matchedSignals.join(', ')}.`
}

function buildReportNarrative(
  focus: ReportTemplateFocus,
  templateSummary: string,
  includesAiAnalysis: boolean,
  recordScope: ReportRecordScope,
  includedRecords: HealthRecord[],
  includedPriorReports: Report[]
): ReportNarrative {
  const includedRecordCount = includedRecords.length
  const includedParsedRecordCount = includedRecords.filter((record) => record.status === 'Parsed').length
  const includedPdfCount = includedRecords.filter((record) => isPdfSourcedRecord(record)).length
  const failedRecordCount = includedRecords.filter((record) => record.status === 'Failed').length
  const parsedCoverageRatio =
    includedRecordCount > 0 ? Math.round((includedParsedRecordCount / includedRecordCount) * 100) : 0
  const focusActions = REPORT_FOCUS_ACTIONS[focus]
  const focusSignalSummary = findFocusSignalSummary(focus, includedRecords)
  const focusDescriptor = focus === 'All' ? 'comprehensive' : focus.toLowerCase()
  const sourceCoverage = includedRecords.slice(0, 5).map(toRecordCoverageLabel)

  if (includedRecords.length > 5) {
    sourceCoverage.push(`+${includedRecords.length - 5} additional record(s)`)
  }
  if (sourceCoverage.length === 0) {
    sourceCoverage.push('No records matched current filters.')
  }

  const priorContext = includedPriorReports
    .slice(0, 4)
    .map((report) => `${report.focus}: ${report.title} (${report.createdAt})`)
  if (includedPriorReports.length > 4) {
    priorContext.push(`+${includedPriorReports.length - 4} additional prior report(s)`)
  }

  if (!includesAiAnalysis) {
    return {
      headline: `Structured ${focusDescriptor} report generated without AI interpretation.`,
      findings: [
        {
          detail:
            `Compiled ${includedRecordCount} record(s) via ${REPORT_RECORD_SCOPE_LABELS[recordScope]} ` +
            `with ${includedParsedRecordCount} parsed records available for manual review.`,
          tone: includedParsedRecordCount > 0 ? 'neutral' : 'watch',
        },
        {
          detail: `PDF coverage in this report: ${includedPdfCount} PDF-sourced record(s).`,
          tone: 'neutral',
        },
        {
          detail:
            failedRecordCount > 0
              ? `${failedRecordCount} included record(s) failed parsing and may limit analysis depth.`
              : 'No parsing failures were detected in the included scope.',
          tone: failedRecordCount > 0 ? 'watch' : 'positive',
        },
      ],
      actions: [
        { detail: 'Review included source records directly with your clinician.', priority: 'high' },
        { detail: 'Enable AI analysis on the next run for synthesized pattern detection.', priority: 'medium' },
        { detail: 'Upload additional parsed records to increase report signal quality.', priority: 'low' },
      ],
      priorContext,
      sourceCoverage,
    }
  }

  const aiFindings: ReportNarrative['findings'] = [
    { detail: templateSummary, tone: 'positive' },
    {
      detail:
        `Coverage: ${includedParsedRecordCount}/${includedRecordCount} parsed records ` +
        `(${parsedCoverageRatio}% parsed success) with ${includedPdfCount} PDF-sourced records.`,
      tone: includedParsedRecordCount > 0 ? 'neutral' : 'watch',
    },
  ]

  if (focusSignalSummary) {
    aiFindings.push({ detail: focusSignalSummary, tone: 'positive' })
  }

  aiFindings.push({
    detail:
      failedRecordCount > 0
        ? `${failedRecordCount} record(s) in scope failed parsing; insights may be conservative until re-processed.`
        : 'No parse-failure blockers detected for the current report scope.',
    tone: failedRecordCount > 0 ? 'watch' : 'positive',
  })

  if (includedPriorReports.length > 0) {
    aiFindings.push({
      detail: `Trend context includes ${includedPriorReports.length} prior report(s).`,
      tone: 'neutral',
    })
  }

  return {
    headline:
      `AI ${focusDescriptor} analysis generated from ${includedRecordCount} record(s) ` +
      `with ${includedPriorReports.length} prior report(s) in context.`,
    findings: aiFindings.slice(0, 5),
    actions: [
      { detail: focusActions.high, priority: 'high' },
      { detail: focusActions.medium, priority: 'medium' },
      { detail: focusActions.low, priority: 'low' },
    ],
    priorContext,
    sourceCoverage,
  }
}

function buildReportSummary(
  includesAiAnalysis: boolean,
  narrative: ReportNarrative,
  recordScope: ReportRecordScope,
  includedRecords: HealthRecord[],
  includedPriorReports: Report[]
): string {
  const includedParsedRecordCount = includedRecords.filter((record) => record.status === 'Parsed').length
  const includedPdfCount = includedRecords.filter((record) => isPdfSourcedRecord(record)).length
  const contextSummary =
    `Scope used ${includedRecords.length} record(s) (${includedParsedRecordCount} parsed, ${includedPdfCount} PDF) ` +
    `via ${REPORT_RECORD_SCOPE_LABELS[recordScope]} and ${includedPriorReports.length} prior report(s).`

  if (!includesAiAnalysis) {
    return `${narrative.headline} ${contextSummary}`
  }

  const leadFinding = narrative.findings[0]?.detail ?? ''
  return `${leadFinding} ${contextSummary}`.trim()
}

function normalizeAgentUserId(email: string, isMock: boolean): string {
  if (isMock) {
    return 'mock@alberto.local'
  }

  return email.trim().toLowerCase()
}

function deriveRecordTitleFromFileName(fileName: string): string {
  const trimmed = fileName.trim()
  if (!trimmed) {
    throw new Error('[UPLOAD_TITLE_REQUIRED] Unable to derive record title from an empty file name.')
  }

  const withoutExtension = trimmed.replace(/\.[^.]+$/, '')
  const normalized = withoutExtension.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
  if (!normalized) {
    throw new Error('[UPLOAD_TITLE_REQUIRED] Unable to derive record title from file name.')
  }

  return normalized
}

function resolveUploadTitle(overrideTitle: string | undefined, fileName: string): string {
  const normalizedOverride = typeof overrideTitle === 'string' ? overrideTitle.trim() : ''
  if (normalizedOverride) {
    return normalizedOverride
  }

  return deriveRecordTitleFromFileName(fileName)
}

function resolveUploadSource(overrideSource: string | undefined): string {
  const normalizedOverride = typeof overrideSource === 'string' ? overrideSource.trim() : ''
  if (normalizedOverride) {
    return normalizedOverride
  }

  return DEFAULT_UPLOAD_SOURCE
}

function resolveDoctorById(doctorId: string) {
  return AVAILABLE_DOCTORS.find((doctor) => doctor.id === doctorId) ?? null
}

export default function useDashboardState(authMode: AuthMode, userEmail: string, userName: string): DashboardState {
  const isMock = authMode === 'mock'
  const agentApiEnabled = isAgentApiEnabled()
  const agentTenantId = 'local'
  const agentUserId = normalizeAgentUserId(userEmail, isMock)
  const defaultOpenRouterSettings = useMemo(() => resolveDefaultOpenRouterSettings(isMock), [isMock])
  const persistKeyFor = (stateKey: string) => (isMock ? null : stateKey)
  const email = isMock ? null : userEmail
  const llmPersistKey = isMock ? 'mockOpenRouterSettings' : 'openRouterSettings'
  const llmPersistEmail = isMock ? 'mock@alberto.local' : userEmail
  const profilePersistKey = isMock ? 'mockPersonalProfile' : 'personalProfile'
  const profilePersistEmail = isMock ? 'mock@alberto.local' : userEmail
  const defaultPersonalProfile = useMemo(() => createDefaultPersonalProfile(userName), [userName])

  const [providers, setProviders] = usePersistedState<Provider[]>(isMock ? INITIAL_PROVIDERS : EMPTY_PROVIDERS, persistKeyFor('providers'), email)
  const [recordsState, setRecords] = usePersistedState<PersistedHealthRecord[]>(isMock ? INITIAL_RECORDS : EMPTY_RECORDS, persistKeyFor('records'), email)
  const [reports, setReports] = usePersistedState<Report[]>(isMock ? INITIAL_REPORTS : EMPTY_REPORTS, persistKeyFor('reports'), email)
  const [tasks, setTasks] = usePersistedState<Task[]>(isMock ? INITIAL_TASKS : EMPTY_TASKS, persistKeyFor('tasks'), email)
  const [legacyChatMessages, setLegacyChatMessages] = usePersistedState<ChatMessage[]>(
    isMock ? INITIAL_CHAT_MESSAGES : EMPTY_CHAT_MESSAGES,
    persistKeyFor('chatMessages'),
    email
  )
  const [chatThreads, setChatThreads] = usePersistedState<ChatThread[]>(
    isMock ? INITIAL_MOCK_CHAT_THREADS : EMPTY_CHAT_THREADS,
    persistKeyFor('chatThreads'),
    email
  )
  const [activeChatThreadId, setActiveChatThreadId] = usePersistedState<string>(
    isMock ? MOCK_CHAT_THREAD_ID : '',
    persistKeyFor('activeChatThreadId'),
    email
  )
  const [consultations, setConsultations] = usePersistedState<Consultation[]>(isMock ? INITIAL_CONSULTATIONS : EMPTY_CONSULTATIONS, persistKeyFor('consultations'), email)
  const [medications, setMedications] = usePersistedState<Medication[]>(isMock ? INITIAL_MEDICATIONS : EMPTY_MEDICATIONS, persistKeyFor('medications'), email)
  const [openRouterSettings, setOpenRouterSettings] = usePersistedState<OpenRouterSettings>(
    defaultOpenRouterSettings,
    llmPersistKey,
    llmPersistEmail
  )
  const effectiveOpenRouterSettings = useMemo(
    () => resolveEffectiveOpenRouterSettings(openRouterSettings, defaultOpenRouterSettings),
    [defaultOpenRouterSettings, openRouterSettings]
  )
  const [personalProfile, setPersonalProfile] = usePersistedState<PersonalProfile>(
    defaultPersonalProfile,
    profilePersistKey,
    profilePersistEmail
  )

  const [activeTab, setActiveTab] = useState<DashboardTabId>(DASHBOARD_DEFAULT_ACTIVE_TAB)
  const [chatLoading, setChatLoading] = useState(false)

  useEffect(() => {
    if (chatThreads.length > 0) {
      return
    }

    const initialMessages = legacyChatMessages
    const initialTitle = initialMessages.length > 0 ? 'Chat 1' : 'New chat'
    const initialThread = createChatThread(initialTitle, initialMessages)
    setChatThreads([initialThread])
    setActiveChatThreadId(initialThread.id)
  }, [chatThreads.length, legacyChatMessages, setChatThreads, setActiveChatThreadId])

  useEffect(() => {
    if (chatThreads.length === 0) {
      return
    }

    const hasActiveThread = chatThreads.some((thread) => thread.id === activeChatThreadId)
    if (!hasActiveThread) {
      setActiveChatThreadId(chatThreads[0].id)
    }
  }, [chatThreads, activeChatThreadId, setActiveChatThreadId])

  const activeChatThread = useMemo(() => {
    if (chatThreads.length === 0) {
      return null
    }

    return chatThreads.find((thread) => thread.id === activeChatThreadId) ?? chatThreads[0]
  }, [chatThreads, activeChatThreadId])

  const chatMessages = useMemo(
    () => activeChatThread?.messages ?? EMPTY_CHAT_MESSAGES,
    [activeChatThread]
  )

  useEffect(() => {
    setLegacyChatMessages(chatMessages)
  }, [chatMessages, setLegacyChatMessages])

  const setChatMessages = useCallback((updater: SetStateAction<ChatMessage[]>) => {
    setChatThreads((currentThreads) => {
      if (currentThreads.length === 0) {
        const nextMessages =
          typeof updater === 'function' ? (updater as (previous: ChatMessage[]) => ChatMessage[])([]) : updater
        const fallbackThread = createChatThread('Chat 1', nextMessages)
        return [fallbackThread]
      }

      const targetThreadId = currentThreads.some((thread) => thread.id === activeChatThreadId)
        ? activeChatThreadId
        : currentThreads[0].id

      const nextThreads = currentThreads.map((thread) => {
        if (thread.id !== targetThreadId) {
          return thread
        }

        const nextMessages =
          typeof updater === 'function'
            ? (updater as (previous: ChatMessage[]) => ChatMessage[])(thread.messages)
            : updater

        return updateChatThreadMessages(thread, nextMessages)
      })

      const targetIndex = nextThreads.findIndex((thread) => thread.id === targetThreadId)
      if (targetIndex <= 0) {
        return nextThreads
      }

      const [targetThread] = nextThreads.splice(targetIndex, 1)
      nextThreads.unshift(targetThread)
      return nextThreads
    })
  }, [activeChatThreadId, setChatThreads])

  const records = useMemo<HealthRecord[]>(
    () => recordsState.map(normalizeRecord),
    [recordsState]
  )
  const isOpenRouterConfigured = useMemo(
    () => hasOpenRouterCredentials(effectiveOpenRouterSettings),
    [effectiveOpenRouterSettings]
  )
  const openRouterSyncSignatureRef = useRef<string | null>(null)

  useEffect(() => {
    if (!agentApiEnabled || !isOpenRouterConfigured) {
      openRouterSyncSignatureRef.current = null
      return
    }

    const syncSignature = `${agentTenantId}::${agentUserId}::${effectiveOpenRouterSettings.model.trim()}::${normalizeOpenRouterApiKey(effectiveOpenRouterSettings.apiKey)}`
    if (openRouterSyncSignatureRef.current === syncSignature) {
      return
    }

    openRouterSyncSignatureRef.current = syncSignature

    void syncSettingsWithAgentApi({
      settings: effectiveOpenRouterSettings,
      tenantId: agentTenantId,
      userId: agentUserId,
    }).catch((error) => {
      openRouterSyncSignatureRef.current = null
      console.warn('Failed to synchronize persisted OpenRouter settings to agent API.', error)
    })
  }, [agentApiEnabled, agentTenantId, agentUserId, effectiveOpenRouterSettings, isOpenRouterConfigured])

  const handleSendMessage = useCallback(
    async (text: string) => {
      const input = text.trim()
      if (!input || chatLoading) {
        return
      }

      const connectedProviders = providers.filter((provider) => provider.connected).length
      const parsedRecords = records.filter((record) => record.status === 'Parsed').length
      const reportsCount = reports.length
      const parsedRecordContext = buildParsedRecordContext(records)

      const userMessage: ChatMessage = {
        id: createEntityId('msg-user'),
        role: 'user',
        content: input,
        timestamp: formatTimestampLabel(new Date()),
      }

      setChatMessages((previous) => [...previous, userMessage])
      setChatLoading(true)

      let assistantContent = ''

      try {
        if (agentApiEnabled) {
          if (!hasOpenRouterCredentials(effectiveOpenRouterSettings)) {
            throw new Error(
              '[OPENROUTER_NOT_CONFIGURED] OpenRouter settings are required. Save API key and model in Settings.'
            )
          }

          const workspaceContext = buildAgentWorkspaceContext(
            records,
            connectedProviders,
            parsedRecords,
            reportsCount,
            parsedRecordContext
          )

          assistantContent = await requestChatAnswerViaAgentApi({
            context: workspaceContext,
            tenantId: agentTenantId,
            text: input,
            userId: agentUserId,
          })
        } else {
          if (!hasOpenRouterCredentials(effectiveOpenRouterSettings)) {
            throw new Error(
              '[OPENROUTER_NOT_CONFIGURED] OpenRouter settings are required. Save API key and model in Settings.'
            )
          }

          const contextMessages = chatMessages.slice(-10).map((message) => ({
            role: message.role,
            content: message.content,
          }))

          assistantContent = await requestOpenRouterCompletion({
            apiKey: normalizeOpenRouterApiKey(effectiveOpenRouterSettings.apiKey),
            model: effectiveOpenRouterSettings.model.trim(),
            messages: [
              {
                role: 'system',
                content: buildOpenRouterSystemPrompt(
                  isMock,
                  connectedProviders,
                  parsedRecords,
                  reportsCount,
                  parsedRecordContext
                ),
              },
              ...contextMessages,
              { role: 'user', content: input },
            ],
          })
        }
      } catch (error) {
        assistantContent = formatDeterministicError(
          error,
          'CHAT_REQUEST_FAILED',
          'Chat request failed.'
        )
      } finally {
        setChatLoading(false)
      }

      const assistantMessage: ChatMessage = {
        id: createEntityId('msg-ai'),
        role: 'assistant',
        content: assistantContent,
        timestamp: formatTimestampLabel(new Date()),
      }

      setChatMessages((previous) => [...previous, assistantMessage])
    },
    [
      chatLoading,
      chatMessages,
      agentApiEnabled,
      agentTenantId,
      agentUserId,
      isMock,
      effectiveOpenRouterSettings,
      providers,
      records,
      reports.length,
      setChatMessages,
    ]
  )

  const startNewChat = useCallback(() => {
    setChatLoading(false)
    const nextThread = createChatThread(
      `Chat ${chatThreads.length + 1}`,
      [createAssistantMessage('Started a new chat. Ask me about labs, symptoms, meds, or trends.')]
    )
    setChatThreads((current) => [nextThread, ...current])
    setActiveChatThreadId(nextThread.id)
  }, [chatThreads.length, setChatLoading, setChatThreads, setActiveChatThreadId])

  const clearAllChats = useCallback(() => {
    const resetThread = createChatThread('Chat 1', EMPTY_CHAT_MESSAGES)
    setChatLoading(false)
    setChatThreads([resetThread])
    setActiveChatThreadId(resetThread.id)
    setLegacyChatMessages(EMPTY_CHAT_MESSAGES)
  }, [setChatLoading, setChatThreads, setActiveChatThreadId, setLegacyChatMessages])

  const resetChats = useCallback(() => {
    const fallbackMessages = [
      createAssistantMessage('Chat reset complete. Tell me what you want to review next.'),
    ]
    const baselineMessages = isMock ? INITIAL_CHAT_MESSAGES : EMPTY_CHAT_MESSAGES

    setChatLoading(false)
    setChatMessages(baselineMessages.length > 0 ? baselineMessages : fallbackMessages)
  }, [isMock, setChatLoading, setChatMessages])

  const switchChatThread = useCallback((threadId: string) => {
    const normalizedThreadId = threadId.trim()
    if (!normalizedThreadId) {
      return
    }

    const targetThread = chatThreads.find((thread) => thread.id === normalizedThreadId)
    if (!targetThread) {
      return
    }

    setChatLoading(false)
    setActiveChatThreadId(targetThread.id)
  }, [chatThreads, setChatLoading, setActiveChatThreadId])

  const renameChatThread = useCallback((threadId: string, nextTitle: string) => {
    const normalizedThreadId = threadId.trim()
    const normalizedTitle = normalizeChatThreadTitle(nextTitle, '')
    if (!normalizedThreadId || !normalizedTitle) {
      return
    }

    setChatThreads((currentThreads) =>
      currentThreads.map((thread) => {
        if (thread.id !== normalizedThreadId) {
          return thread
        }

        return {
          ...thread,
          title: normalizedTitle,
          updatedAt: new Date().toISOString(),
        }
      })
    )
  }, [setChatThreads])

  const deleteChatThread = useCallback((threadId: string) => {
    const normalizedThreadId = threadId.trim()
    if (!normalizedThreadId) {
      return
    }

    const targetIndex = chatThreads.findIndex((thread) => thread.id === normalizedThreadId)
    if (targetIndex < 0) {
      return
    }

    setChatLoading(false)

    if (chatThreads.length <= 1) {
      const fallbackMessages = isMock ? INITIAL_CHAT_MESSAGES : EMPTY_CHAT_MESSAGES
      const resetThread: ChatThread = {
        ...chatThreads[0],
        title: 'Chat 1',
        messages: fallbackMessages,
        updatedAt: new Date().toISOString(),
      }
      setChatThreads([resetThread])
      setActiveChatThreadId(resetThread.id)
      return
    }

    const remainingThreads = chatThreads.filter((thread) => thread.id !== normalizedThreadId)
    setChatThreads(remainingThreads)

    if (activeChatThreadId === normalizedThreadId) {
      const nextActiveThread = remainingThreads[Math.min(targetIndex, remainingThreads.length - 1)] ?? remainingThreads[0]
      if (nextActiveThread) {
        setActiveChatThreadId(nextActiveThread.id)
      }
    }
  }, [activeChatThreadId, chatThreads, isMock, setChatLoading, setChatThreads, setActiveChatThreadId])

  const handleAddRecord = useCallback(
    async (draft: UploadDraft): Promise<ActionResult> => {
      const file = draft.file ?? null
      if (!file) {
        return { ok: false, error: '[UPLOAD_FILE_REQUIRED] A PDF file is required for upload parsing.' }
      }

      let normalizedTitle = ''
      let normalizedSource = ''
      const normalizedKindForAgent = draft.kind ?? DEFAULT_UPLOAD_KIND
      try {
        normalizedTitle = resolveUploadTitle(draft.title, file.name)
        normalizedSource = resolveUploadSource(draft.source)
      } catch (error) {
        const reason = formatDeterministicError(
          error,
          'UPLOAD_METADATA_INVALID',
          'Upload metadata is invalid.'
        )
        return { ok: false, error: reason }
      }

      const queuedAt = new Date()
      if (agentApiEnabled && !hasOpenRouterCredentials(effectiveOpenRouterSettings)) {
        const reason =
          '[OPENROUTER_NOT_CONFIGURED] OpenRouter settings are not configured. Save API key and model in Settings.'
        return { ok: false, error: reason }
      }

      const requestedRecordId = typeof draft.id === 'string' ? draft.id.trim() : ''
      const pendingRecordId =
        requestedRecordId && records.every((record) => record.id !== requestedRecordId)
          ? requestedRecordId
          : createEntityId('record')

      const pendingRecord: HealthRecord = {
        id: pendingRecordId,
        kind: normalizedKindForAgent,
        source: normalizedSource,
        status: 'Parsing',
        title: normalizedTitle,
        updatedAt: `Queued at ${formatClockTime(queuedAt)}`,
        fileSizeBytes: typeof draft.fileSizeBytes === 'number' ? draft.fileSizeBytes : file.size,
        parseError: null,
        parseSummary: null,
        textPreview: null,
      }

      setRecords((current) => [pendingRecord, ...current.map(normalizeRecord)])

      try {
        const parsed = agentApiEnabled
          ? await uploadAndParseRecordViaAgentApi({
              file,
              kind: normalizedKindForAgent,
              source: normalizedSource,
              tenantId: agentTenantId,
              title: normalizedTitle,
              userId: agentUserId,
            })
          : await parseUploadedPdfRecord({
              file,
              kindHint: draft.kind,
              openRouterSettings: effectiveOpenRouterSettings,
              source: draft.source,
              title: draft.title,
              useOpenRouter: isOpenRouterConfigured,
            })

        setRecords((current) =>
          current.map((record) => {
            const normalizedRecord = normalizeRecord(record)
            if (normalizedRecord.id !== pendingRecord.id) {
              return normalizedRecord
            }

            const parsedAt = new Date()
            return {
              ...normalizedRecord,
              title: parsed.title,
              kind: parsed.detectedKind,
              source: parsed.source,
              status: 'Parsed',
              updatedAt: formatRelativeTimestamp(parsedAt),
              parseError: null,
              parseSummary: parsed.parseSummary,
              textPreview: parsed.textPreview,
            }
          })
        )

        return { ok: true }
      } catch (error) {
        const reason = formatDeterministicError(
          error,
          'UPLOAD_PARSE_FAILED',
          'Upload parsing failed.'
        )

        setRecords((current) =>
          current.map((record) => {
            const normalizedRecord = normalizeRecord(record)
            if (normalizedRecord.id !== pendingRecord.id) {
              return normalizedRecord
            }

            return {
              ...normalizedRecord,
              status: 'Failed',
              updatedAt: `${formatRelativeTimestamp(new Date())} (parse failed)`,
              parseError: reason,
              parseSummary: 'The uploaded PDF could not be parsed.',
              textPreview: null,
            }
          })
        )

        return { ok: false, error: reason }
      }
    },
    [
      agentApiEnabled,
      agentTenantId,
      agentUserId,
      effectiveOpenRouterSettings,
      isOpenRouterConfigured,
      records,
      setRecords,
    ]
  )

  const handleDeleteRecord = useCallback(
    (recordId: string): ActionResult => {
      const normalizedRecordId = recordId.trim()
      if (!normalizedRecordId) {
        return { ok: false, error: '[RECORD_ID_REQUIRED] Record id is required.' }
      }

      const targetRecord = records.find((record) => record.id === normalizedRecordId)
      if (!targetRecord) {
        return { ok: false, error: `[RECORD_NOT_FOUND] Record "${normalizedRecordId}" was not found.` }
      }

      setRecords((current) =>
        current.filter((record) => normalizeRecord(record).id !== normalizedRecordId)
      )

      return { ok: true }
    },
    [records, setRecords]
  )

  const handleGenerateReport = useCallback(
    async (request: ReportGenerationRequest) => {
      const normalizedFocus = isReportTemplateFocus(request.focus) ? request.focus : DEFAULT_REPORT_FOCUS
      const normalizedRecordScope = isReportRecordScope(request.recordScope)
        ? request.recordScope
        : DEFAULT_REPORT_RECORD_SCOPE
      const includesAiAnalysis = request.includeAiAnalysis !== false
      const includePriorReports = request.includePriorReports === true
      const maxPriorReports = clampPriorReportCount(request.maxPriorReports)
      const priorReportsDateFrom = includePriorReports
        ? normalizeReportDateInput(request.priorReportsDateFrom)
        : null
      const priorReportsDateTo = includePriorReports
        ? normalizeReportDateInput(request.priorReportsDateTo)
        : null
      const priorReportsFromBoundary = parseReportDateBoundary(priorReportsDateFrom, 'start')
      const priorReportsToBoundary = parseReportDateBoundary(priorReportsDateTo, 'end')

      if (
        includePriorReports &&
        priorReportsFromBoundary &&
        priorReportsToBoundary &&
        priorReportsFromBoundary.getTime() > priorReportsToBoundary.getTime()
      ) {
        throw new Error('[REPORT_DATE_RANGE_INVALID] Start date must be before end date.')
      }

      const includedRecords = records.filter((record) =>
        matchesReportRecordScope(record, normalizedRecordScope)
      )
      const usedPriorReportsDateRange = includePriorReports
        ? Boolean(priorReportsFromBoundary || priorReportsToBoundary)
        : false
      const includedPriorReports = includePriorReports
        ? reports
            .filter((report) => {
              if (!usedPriorReportsDateRange) {
                return true
              }

              const reportTimestamp = parseReportIsoTimestamp(report)
              if (!reportTimestamp) {
                return false
              }

              const reportTime = reportTimestamp.getTime()
              if (priorReportsFromBoundary && reportTime < priorReportsFromBoundary.getTime()) {
                return false
              }
              if (priorReportsToBoundary && reportTime > priorReportsToBoundary.getTime()) {
                return false
              }

              return true
            })
            .slice(0, maxPriorReports)
        : []

      const template = REPORT_TEMPLATES[normalizedFocus]
      const confidence = includesAiAnalysis
        ? computeDeterministicReportConfidence(normalizedFocus, reports.length)
        : 0
      const includedParsedRecordCount = includedRecords.filter((record) => record.status === 'Parsed').length
      const includedPdfCount = includedRecords.filter((record) => isPdfSourcedRecord(record)).length
      const narrative = buildReportNarrative(
        normalizedFocus,
        template.summary,
        includesAiAnalysis,
        normalizedRecordScope,
        includedRecords,
        includedPriorReports
      )
      const generatedAt = new Date()
      const generatedReport: Report = {
        id: createEntityId('report'),
        title: template.title,
        focus: normalizedFocus,
        confidence,
        summary: buildReportSummary(
          includesAiAnalysis,
          narrative,
          normalizedRecordScope,
          includedRecords,
          includedPriorReports
        ),
        narrative,
        createdAt: formatRelativeTimestamp(generatedAt),
        createdAtIso: generatedAt.toISOString(),
        includesAiAnalysis,
        generationConfig: {
          includeAiAnalysis: includesAiAnalysis,
          includePriorReports,
          maxPriorReports,
          priorReportsDateFrom,
          priorReportsDateTo,
          recordScope: normalizedRecordScope,
        },
        generationContext: {
          includedPdfCount,
          includedParsedRecordCount,
          includedPriorReportCount: includedPriorReports.length,
          includedRecordCount: includedRecords.length,
          usedPriorReportsDateRange,
        },
      }

      setReports((current) => [generatedReport, ...current])
    },
    [records, reports, setReports]
  )

  const handleUpdateReportMetadata = useCallback(
    (
      reportId: string,
      metadata: { createdAt: string; focus: string; title: string }
    ): ActionResult => {
      const normalizedReportId = reportId.trim()
      if (!normalizedReportId) {
        return { ok: false, error: '[REPORT_ID_REQUIRED] Report id is required.' }
      }

      const targetReport = reports.find((report) => report.id === normalizedReportId)
      if (!targetReport) {
        return { ok: false, error: `[REPORT_NOT_FOUND] Report "${normalizedReportId}" was not found.` }
      }

      const normalizedTitle = metadata.title.trim()
      const normalizedFocus = metadata.focus.trim()
      const normalizedCreatedAt = metadata.createdAt.trim()

      if (!normalizedTitle) {
        return { ok: false, error: '[REPORT_TITLE_REQUIRED] Report title is required.' }
      }
      if (!normalizedFocus) {
        return { ok: false, error: '[REPORT_FOCUS_REQUIRED] Report focus is required.' }
      }
      if (!normalizedCreatedAt) {
        return { ok: false, error: '[REPORT_DATE_REQUIRED] Report date label is required.' }
      }
      if (!isReportTemplateFocus(normalizedFocus)) {
        return {
          ok: false,
          error: `[REPORT_FOCUS_INVALID] Report focus "${normalizedFocus}" is not supported.`,
        }
      }

      setReports((current) =>
        current.map((report) =>
          report.id === normalizedReportId
            ? {
                ...report,
                title: normalizedTitle,
                focus: normalizedFocus,
                createdAt: normalizedCreatedAt,
              }
            : report
        )
      )

      return { ok: true }
    },
    [reports, setReports]
  )

  const toggleProvider = useCallback(
    async (providerId: string) => {
      const targetProvider = providers.find((provider) => provider.id === providerId)
      if (!targetProvider) {
        throw new Error(`[PROVIDER_NOT_FOUND] Provider "${providerId}" was not found.`)
      }

      await new Promise((resolve) => {
        window.setTimeout(resolve, PROVIDER_SYNC_DELAY_MS)
      })

      const syncTimestamp = formatRelativeTimestamp(new Date())
      setProviders((current) =>
        current.map((provider) => {
          if (provider.id !== providerId) {
            return provider
          }

          const nextConnected = !provider.connected
          return {
            ...provider,
            connected: nextConnected,
            lastSync: nextConnected ? syncTimestamp : PROVIDER_DISCONNECTED_LABEL,
          }
        })
      )
    },
    [providers, setProviders]
  )

  const handleAddCustomProvider = useCallback(
    (providerName: string): ActionResult => {
      const normalizedName = providerName.trim()
      if (!normalizedName) {
        return { ok: false, error: '[PROVIDER_NAME_REQUIRED] Provider name is required.' }
      }

      const normalizedForCompare = normalizedName.toLowerCase()
      if (providers.some((provider) => provider.name.trim().toLowerCase() === normalizedForCompare)) {
        return {
          ok: false,
          error: `[PROVIDER_DUPLICATE] Provider "${normalizedName}" already exists.`,
        }
      }

      const nextProvider: Provider = {
        id: createEntityId('provider'),
        name: normalizedName,
        type: 'Custom',
        connected: false,
        lastSync: PROVIDER_DISCONNECTED_LABEL,
      }

      setProviders((current) => [...current, nextProvider])
      return { ok: true }
    },
    [providers, setProviders]
  )

  const toggleTask = useCallback((taskId: string) => {
    setTasks((current) =>
      current.map((task) => (task.id === taskId ? { ...task, done: !task.done } : task))
    )
  }, [setTasks])

  const handleBookConsultation = useCallback(
    (draft: ConsultationDraft): ActionResult => {
      const doctorId = draft.doctorId.trim()
      const reason = draft.reason.trim()
      const timeSlot = draft.timeSlot.trim()

      if (!doctorId) {
        return { ok: false, error: '[CONSULT_DOCTOR_REQUIRED] Choose a doctor before booking.' }
      }
      if (!reason) {
        return { ok: false, error: '[CONSULT_REASON_REQUIRED] Choose a consultation reason before booking.' }
      }
      if (!timeSlot) {
        return { ok: false, error: '[CONSULT_TIME_REQUIRED] Choose a time slot before booking.' }
      }

      const doctor = resolveDoctorById(doctorId)
      if (!doctor) {
        return { ok: false, error: `[CONSULT_DOCTOR_INVALID] Doctor "${doctorId}" is not available.` }
      }
      if (!doctor.available) {
        return {
          ok: false,
          error: `[CONSULT_DOCTOR_UNAVAILABLE] ${doctor.name} is currently unavailable. Choose another provider.`,
        }
      }

      const consultationId = createEntityId('consult')
      const consultation: Consultation = {
        id: consultationId,
        doctorId: doctor.id,
        doctorName: doctor.name,
        date: new Date().toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }),
        time: timeSlot,
        reason,
        status: 'upcoming',
        joinUrl: `https://meet.albertohealth.demo/${consultationId}`,
      }

      setConsultations((current) => [consultation, ...current])
      return { ok: true }
    },
    [setConsultations]
  )

  const handleAddMedication = useCallback(
    (draft: MedicationDraft) => {
      if (!draft.name.trim() || !draft.dosage.trim()) {
        return
      }

      const medication: Medication = {
        id: createEntityId('med'),
        name: draft.name.trim(),
        dosage: draft.dosage.trim(),
        frequency: draft.frequency,
        timeOfDay: draft.timeOfDay,
        prescriber: 'Self',
        startDate: new Date().toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }),
        refillDate: 'TBD',
        status: 'active',
        notes: '',
      }

      setMedications((current) => [medication, ...current])
    },
    [setMedications]
  )

  const saveOpenRouterSettings = useCallback(
    async (settings: OpenRouterSettings) => {
      const normalizedModel = settings.model.trim()
      if (!normalizedModel) {
        throw new Error('[SETTINGS_MODEL_REQUIRED] OpenRouter model is required.')
      }

      const previousSettings = openRouterSettings
      const nextSettings: OpenRouterSettings = {
        apiKey: normalizeOpenRouterApiKey(settings.apiKey),
        model: normalizedModel,
      }

      setOpenRouterSettings(nextSettings)

      if (!agentApiEnabled) {
        return
      }

      try {
        await syncSettingsWithAgentApi({
          settings: nextSettings,
          tenantId: agentTenantId,
          userId: agentUserId,
        })
      } catch (error) {
        setOpenRouterSettings(previousSettings)
        throw error
      }
    },
    [agentApiEnabled, agentTenantId, agentUserId, openRouterSettings, setOpenRouterSettings]
  )

  const clearOpenRouterSettings = useCallback(
    async (model: string) => {
      const normalizedModel = model.trim() || DEFAULT_OPENROUTER_MODEL
      const previousSettings = openRouterSettings
      const nextSettings: OpenRouterSettings = {
        apiKey: defaultOpenRouterSettings.apiKey,
        model: normalizedModel,
      }

      setOpenRouterSettings(nextSettings)

      if (!agentApiEnabled) {
        return
      }

      try {
        await syncSettingsWithAgentApi({
          settings: nextSettings,
          tenantId: agentTenantId,
          userId: agentUserId,
        })
      } catch (error) {
        setOpenRouterSettings(previousSettings)
        throw error
      }
    },
    [agentApiEnabled, agentTenantId, agentUserId, defaultOpenRouterSettings.apiKey, openRouterSettings, setOpenRouterSettings]
  )

  const savePersonalProfile = useCallback(
    (profile: PersonalProfile) => {
      setPersonalProfile(normalizePersonalProfile(profile, userName))
    },
    [setPersonalProfile, userName]
  )

  const resetLocalStorage = useCallback(() => {
    clearAppLocalStorage()

    setProviders(isMock ? INITIAL_PROVIDERS : EMPTY_PROVIDERS)
    setRecords(isMock ? INITIAL_RECORDS : EMPTY_RECORDS)
    setReports(isMock ? INITIAL_REPORTS : EMPTY_REPORTS)
    setTasks(isMock ? INITIAL_TASKS : EMPTY_TASKS)
    const baselineMessages = isMock ? INITIAL_CHAT_MESSAGES : EMPTY_CHAT_MESSAGES
    const resetThread = createChatThread('Chat 1', baselineMessages)
    setChatThreads([resetThread])
    setActiveChatThreadId(resetThread.id)
    setLegacyChatMessages(baselineMessages)
    setConsultations(isMock ? INITIAL_CONSULTATIONS : EMPTY_CONSULTATIONS)
    setMedications(isMock ? INITIAL_MEDICATIONS : EMPTY_MEDICATIONS)
    setActiveTab(DASHBOARD_DEFAULT_ACTIVE_TAB)
    setChatLoading(false)
  }, [
    isMock,
    setProviders,
    setRecords,
    setReports,
    setTasks,
    setChatThreads,
    setActiveChatThreadId,
    setLegacyChatMessages,
    setConsultations,
    setMedications,
    setActiveTab,
    setChatLoading,
  ])

  const metrics = useMemo(
    () => computeDashboardMetrics(providers, records, reports, tasks, isMock),
    [providers, records, reports, tasks, isMock]
  )

  return {
    activeTab,
    activeChatThreadId,
    agentApiEnabled,
    setActiveTab,
    metrics,
    providers,
    records,
    reports,
    tasks,
    chatThreads,
    chatMessages,
    chatLoading,
    consultations,
    medications,
    openRouterSettings: effectiveOpenRouterSettings,
    personalProfile,
    isOpenRouterConfigured,
    actions: {
      clearAllChats,
      clearOpenRouterSettings,
      deleteChatThread,
      handleAddCustomProvider,
      handleDeleteRecord,
      handleAddMedication,
      handleAddRecord,
      handleBookConsultation,
      handleGenerateReport,
      handleUpdateReportMetadata,
      handleSendMessage,
      renameChatThread,
      resetChats,
      resetLocalStorage,
      savePersonalProfile,
      saveOpenRouterSettings,
      startNewChat,
      switchChatThread,
      toggleProvider,
      toggleTask,
    },
  }
}
